import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MapPin, Share2, Navigation, Bookmark, Star } from 'lucide-react';
import { categories, getOfferType } from '../../data/constants';

export const DealCard = ({ deal }) => {
  const [bookmarked, setBookmarked] = useState(false);
  const offerType = getOfferType(deal.offer_type);
  
  useEffect(() => {
    const bookmarks = JSON.parse(localStorage.getItem('bookmarks') || '[]');
    setBookmarked(bookmarks.includes(deal.id));
  }, [deal.id]);

  const handleBookmark = () => {
    const bookmarks = JSON.parse(localStorage.getItem('bookmarks') || '[]');
    if (bookmarked) {
      localStorage.setItem('bookmarks', JSON.stringify(bookmarks.filter(id => id !== deal.id)));
    } else {
      localStorage.setItem('bookmarks', JSON.stringify([...bookmarks, deal.id]));
    }
    setBookmarked(!bookmarked);
    toast.success(bookmarked ? 'Removed from bookmarks' : 'Added to bookmarks');
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/deal/${deal.id}`;
    if (navigator.share) { try { await navigator.share({ title: deal.title, text: deal.description, url }); } catch (e) {} }
    else { navigator.clipboard.writeText(url); toast.success('Link copied!'); }
  };

  const handleNavigate = () => {
    if (deal.maps_link) window.open(deal.maps_link, '_blank');
    else if (deal.latitude && deal.longitude) window.open(`https://www.google.com/maps/dir/?api=1&destination=${deal.latitude},${deal.longitude}`, '_blank');
  };

  const category = categories.find(c => c.id === deal.category) || categories[7];
  const CategoryIcon = category.icon;
  const imageUrl = deal.image_url || category.placeholder;

  return (
    <div className={`bg-white rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300 group ${deal.featured ? 'ring-2 ring-orange-400/50 shadow-md' : 'shadow-sm border border-slate-100'}`} data-testid={`deal-card-${deal.id}`}>
      <Link to={`/deal/${deal.id}`}>
        <div className="relative h-40 bg-slate-100">
          <img src={imageUrl} alt={deal.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.target.src = category.placeholder; }} />
          {/* Featured Badge */}
          {deal.featured && (
            <span className="absolute top-2 right-2 bg-orange-500 text-white text-xs px-2.5 py-1 rounded-md font-medium flex items-center gap-1">
              <Star className="w-3 h-3 fill-white" /> Featured
            </span>
          )}
          {/* Offer Type Badge */}
          <span className="absolute top-2 left-2 bg-white/95 backdrop-blur-sm text-slate-700 text-xs px-2 py-1 rounded-md font-medium">
            {offerType.emoji} {offerType.name}
          </span>
          {deal.distance !== undefined && !deal.featured && (
            <span className="absolute top-2 right-2 bg-white/95 backdrop-blur-sm text-slate-600 text-xs px-2 py-1 rounded-md flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {deal.distance.toFixed(1)} km
            </span>
          )}
        </div>
      </Link>
      
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <Link to={`/deal/${deal.id}`}>
            <h3 className="font-semibold text-slate-800 group-hover:text-orange-600 transition line-clamp-1 text-sm">{deal.title}</h3>
          </Link>
          {deal.price && <span className="text-slate-800 font-bold whitespace-nowrap text-sm">₹{deal.price}</span>}
        </div>
        
        <p className="text-slate-500 text-xs line-clamp-2 mb-3">{deal.description}</p>
        
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
          <span className="flex items-center gap-1">
            <div className={`w-4 h-4 ${category.color} rounded flex items-center justify-center`}><CategoryIcon className="w-2.5 h-2.5 text-white" /></div>
            {category.short || category.name}
          </span>
          <span className="text-slate-300">•</span>
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {deal.area}</span>
        </div>
        
        <div className="flex gap-2">
          <button onClick={handleNavigate} className="flex-1 bg-slate-800 text-white py-2 rounded-lg text-xs font-medium hover:bg-slate-900 transition flex items-center justify-center gap-1.5" data-testid={`navigate-btn-${deal.id}`}>
            <Navigation className="w-3.5 h-3.5" /> Navigate
          </button>
          <button onClick={handleShare} className="p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition" data-testid={`share-btn-${deal.id}`}>
            <Share2 className="w-3.5 h-3.5 text-slate-500" />
          </button>
          <button onClick={handleBookmark} className={`p-2 rounded-lg transition ${bookmarked ? 'bg-orange-50' : 'bg-slate-50 hover:bg-slate-100'}`} data-testid={`bookmark-btn-${deal.id}`}>
            <Bookmark className={`w-3.5 h-3.5 ${bookmarked ? 'text-orange-500 fill-orange-500' : 'text-slate-500'}`} />
          </button>
        </div>
      </div>
    </div>
  );
};
